// **************************************************************************
// VALIDATION TYPES
// **************************************************************************

/**
 * Single validation error with translation key
 */
export interface ValidationError {
    key: string;
    params?: Record<string, unknown>;
}

/**
 * Single validation warning with translation key.
 * Warnings inform the user but do NOT block order creation.
 */
export interface ValidationWarning {
    key: string;
    params?: Record<string, unknown>;
}

/**
 * Quote-level validation errors (from QuoteState data)
 */
export interface QuoteLevelValidation {
    customerErrors: ValidationError[];      // quote.customer
    quoteErrors: ValidationError[];         // quote.salesInfo (säljare, etc)
    deliveryErrors: ValidationError[];      // quote.deliveryInfo (adress, villkor, datum)
    salesSupportErrors: ValidationError[];  // quote.salesInfo/deliveryInfo (för review)
    salesCreditErrors: ValidationError[];   // quote.salesInfo.salesCredits (sum=100, complete rows)
}

/**
 * Version-level validation errors (from Version data)
 */
export interface VersionLevelValidation {
    shippingErrors: ValidationError[];        // version.shipping (fraktkostnader)
    projectErrors: ValidationError[];         // version.projectManagementAndKickback
    itemGroupErrors: ValidationError[];       // version.itemGroups
    itemErrors: ValidationError[];            // version.itemGroups[].items
    subscriptionErrors: ValidationError[];    // version.subscription (ränta, etc.)
    itemWarnings: ValidationWarning[];        // warnings for items (e.g. zero prices)
}

/**
 * Complete validation result stored in QuoteState.
 * Contains error keys (not translated messages).
 */
export interface QuoteValidation {
    quoteValidation: QuoteLevelValidation;
    versionValidation: VersionLevelValidation;
    totalQuoteErrors: number;
    totalVersionErrors: number;
    totalOrderErrorCount: number;
    totalWarningCount: number;
    isValidForOrder: boolean;
}

/**
 * Empty quote-level validation
 */
export const emptyQuoteLevelValidation: QuoteLevelValidation = {
    customerErrors: [],
    quoteErrors: [],
    deliveryErrors: [],
    salesSupportErrors: [],
    salesCreditErrors: [],
};

/**
 * Empty version-level validation
 */
export const emptyVersionLevelValidation: VersionLevelValidation = {
    shippingErrors: [],
    projectErrors: [],
    itemGroupErrors: [],
    itemErrors: [],
    subscriptionErrors: [],
    itemWarnings: [],
};

/**
 * Empty validation result
 */
export const emptyQuoteValidation: QuoteValidation = {
    quoteValidation: emptyQuoteLevelValidation,
    versionValidation: emptyVersionLevelValidation,
    totalQuoteErrors: 0,
    totalVersionErrors: 0,
    totalOrderErrorCount: 0,
    totalWarningCount: 0,
    isValidForOrder: true,
};
